// table — wireframe skin module (RUN-407: native table redesign)
// Native <table> structure with wireframe color tokens.
// Token disposition: color tokens overridden to wf-* namespace; spacing/sizing pass-through

const _MANUAL_SLOT_SUPPORTED =
  typeof HTMLSlotElement !== 'undefined' &&
  typeof HTMLSlotElement.prototype.assign === 'function';

const SVG_UNSORTED = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2L9 5H3L6 2Z" fill="currentColor"/><path d="M6 10L3 7H9L6 10Z" fill="currentColor"/></svg>`;
const SVG_ASC     = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2L10 7H2L6 2Z" fill="currentColor"/></svg>`;
const SVG_DESC    = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 10L2 5H10L6 10Z" fill="currentColor"/></svg>`;

export const styles = `
  :host {
    display: block;
    position: relative;
    background: var(--run-wf-surface);
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto;
    font-family: var(--run-wf-font-family);
    font-weight: 400;
  }

  [part="base"] {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }

  :host([bordered]) [part="base"] {
    border: 1px dashed var(--run-wf-border);
  }

  [part="loading-overlay"] {
    display: none;
    position: absolute;
    inset: 0;
    background: var(--run-wf-overlay);
    z-index: 1;
  }

  :host([loading]) [part="loading-overlay"] {
    display: block;
  }

  [part="th"],
  [part="th-select"] {
    padding: var(--_run-table-cell-padding-v, 8px) var(--_run-table-cell-padding-h, 12px);
    font-size: var(--_run-table-font-size, 14px);
    font-weight: 400;
    font-family: var(--run-wf-font-family, monospace);
    color: var(--run-wf-text, #111);
    background: var(--run-wf-surface, #fff);
    border: 1px solid var(--run-wf-border, #ccc);
    border-bottom: 1px solid var(--run-wf-border, #ccc);
    white-space: nowrap;
    user-select: none;
    text-align: start;
    vertical-align: middle;
  }

  [part="th-select"] {
    width: 40px;
  }

  [part="th"][data-sortable] {
    cursor: pointer;
  }

  .th-content {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .th-label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .th-sort-indicator {
    flex-shrink: 0;
    color: var(--run-wf-text-secondary, #666);
    font-size: 12px;
    line-height: 0;
  }

  [part="th"][data-sort-direction] .th-sort-indicator {
    color: var(--run-wf-text, #111);
  }

  .select-all-inner {
    display: flex;
    align-items: center;
  }

  [part="td"],
  [part="td-select"] {
    padding: var(--_run-table-cell-padding-v, 8px) var(--_run-table-cell-padding-h, 12px);
    font-size: var(--_run-table-font-size, 14px);
    color: var(--run-wf-text, #111);
    background: var(--_run-table-row-bg, var(--run-wf-surface, #fff));
    border-bottom: 1px solid var(--run-wf-border, #ccc);
    vertical-align: middle;
  }

  [part="td-select"] {
    width: 40px;
  }

  [part="tr"][data-selected] [part="td"],
  [part="tr"][data-selected] [part="td-select"] {
    background: var(--run-wf-accent-subtle, #e8eaf6);
  }

  [part="tr"][data-disabled] [part="td"],
  [part="tr"][data-disabled] [part="td-select"] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  [part="tr"][data-clickable]:not([data-disabled]):hover {
    cursor: pointer;
  }

  [part="tr-skeleton"] td {
    padding: 0;
    border-bottom: none;
  }
`;

export function render(host) {
  const { selects, clickable, striped, loading } = host;
  const colEls = host._columns;
  const rowEls = host._rows;

  const hasMultiSelect = selects === 'multiple';
  const colCount = colEls.length;

  // ── <colgroup> ─────────────────────────────────────────────────────────────
  let colgroupHtml = '';
  if (colCount > 0) {
    const colItems = colEls.map((col, i) => {
      const w = (col.width != null ? String(col.width) : null) ?? col.getAttribute('width');
      if (w && /^\d+(\.\d+)?px$/.test(w.trim())) {
        return `<col data-col-idx="${i}" style="width:${w.trim()}">`;
      }
      return `<col data-col-idx="${i}">`;
    }).join('');
    colgroupHtml = `<colgroup>${colItems}</colgroup>`;
  }

  // ── <thead> ────────────────────────────────────────────────────────────────
  let theadHtml = '';
  if (colCount > 0) {
    const selectThHtml = hasMultiSelect ? `
      <th part="th-select" scope="col">
        <div class="select-all-inner">
          <run-checkbox
            part="select-all-checkbox"
            ${host._getSelectAllState() === 'checked' ? 'checked' : ''}
            ${host.selectAllIndeterminate ? 'indeterminate' : ''}
            aria-label="Select all rows"
          ></run-checkbox>
        </div>
      </th>` : '';

    const colHeaders = colEls.map((col, i) => {
      const sortable = col.sortable === true;
      const dir = col.sortDirection;
      const align = col.align || 'start';
      const justifyMap = { start: 'flex-start', center: 'center', end: 'flex-end' };
      const justify = justifyMap[align] || 'flex-start';

      let ariaSort = '';
      let sortSvg = SVG_UNSORTED;
      if (sortable) {
        if (dir === 'asc')       { ariaSort = 'ascending';  sortSvg = SVG_ASC;  }
        else if (dir === 'desc') { ariaSort = 'descending'; sortSvg = SVG_DESC; }
        else                     { ariaSort = 'none'; }
      }

      const attrs = [
        'part="th"', 'scope="col"',
        `data-col-idx="${i}"`, `data-align="${align}"`,
        sortable ? 'data-sortable' : '',
        dir      ? `data-sort-direction="${dir}"` : '',
        ariaSort ? `aria-sort="${ariaSort}"` : '',
        sortable ? 'tabindex="0"' : '',
      ].filter(Boolean).join(' ');

      const sortIndicator = sortable
        ? `<span class="th-sort-indicator">${sortSvg}</span>` : '';

      return `<th ${attrs}><div class="th-content" style="justify-content:${justify}"><span class="th-label"><slot name="col-${i}"></slot></span>${sortIndicator}</div></th>`;
    }).join('');

    theadHtml = `<thead part="thead"><tr part="tr-head">${selectThHtml}${colHeaders}</tr></thead>`;
  }

  // ── <tbody> ────────────────────────────────────────────────────────────────
  let tbodyHtml = '';
  const totalCols = (hasMultiSelect ? 1 : 0) + Math.max(colCount, 1);

  if (loading && rowEls.length === 0) {
    const skeletonRow = `<tr part="tr-skeleton"><td colspan="${totalCols}"><run-skeleton></run-skeleton></td></tr>`;
    tbodyHtml = `<tbody part="tbody">${skeletonRow}${skeletonRow}${skeletonRow}</tbody>`;
  } else if (rowEls.length > 0) {
    const rowParts = rowEls.map((row, ri) => {
      const isSelected = row.selected === true;
      const isDisabled = row.disabled === true;
      let stripeAttr = '';
      if (striped && !isSelected && ri % 2 === 1) {
        stripeAttr = ' style="--_run-table-row-bg: var(--run-wf-surface-subtle, #f5f5f5);"';
      }
      const rowAttrs = [
        'part="tr"', `data-row-idx="${ri}"`,
        isSelected ? 'data-selected' : '',
        isDisabled ? 'data-disabled' : '',
        clickable  ? 'data-clickable' : '',
        isSelected ? 'aria-selected="true"' : (selects ? 'aria-selected="false"' : ''),
        isDisabled ? 'aria-disabled="true"' : '',
      ].filter(Boolean).join(' ');
      const selectTdHtml = hasMultiSelect
        ? `<td part="td-select"><run-checkbox${isSelected ? ' checked' : ''}${isDisabled ? ' disabled' : ''} aria-label="Select row"></run-checkbox></td>` : '';
      const dataCells = colEls.map((col, ci) =>
        `<td part="td" data-row-idx="${ri}" data-col-idx="${ci}"><slot name="row-${ri}-col-${ci}"></slot></td>`
      ).join('');
      return `<tr ${rowAttrs}${stripeAttr}>${selectTdHtml}${dataCells}</tr>`;
    }).join('');
    tbodyHtml = `<tbody part="tbody">${rowParts}</tbody>`;
  }

  host.shadowRoot.innerHTML = `
    <table part="base">${colgroupHtml}${theadHtml}${tbodyHtml}</table>
    <div part="loading-overlay" aria-hidden="true"></div>
  `;

  if (_MANUAL_SLOT_SUPPORTED) {
    colEls.forEach((col, i) => {
      const slot = host.shadowRoot.querySelector(`slot[name="col-${i}"]`);
      if (slot) slot.assign(col);
    });
    rowEls.forEach((row, ri) => {
      const cellEls = Array.from(row.querySelectorAll('run-table-cell'));
      colEls.forEach((_, ci) => {
        const slot = host.shadowRoot.querySelector(`slot[name="row-${ri}-col-${ci}"]`);
        if (slot && cellEls[ci]) slot.assign(cellEls[ci]);
      });
    });
  }

  if (hasMultiSelect) {
    const selectAllCb = host.shadowRoot.querySelector('[part="select-all-checkbox"]');
    if (selectAllCb) {
      selectAllCb.addEventListener('change', (e) => {
        host._handleSelectAllChange(e.target.checked ?? selectAllCb.checked);
      });
    }
    const rowCbs = host.shadowRoot.querySelectorAll('[part="td-select"] run-checkbox');
    rowEls.forEach((row, ri) => {
      const cb = rowCbs[ri];
      if (cb) {
        cb.addEventListener('change', (e) => {
          host._handleRowCheckboxChange(row, e.target.checked ?? cb.checked);
        });
      }
    });
  }

  colEls.forEach((col, ci) => {
    if (!col.sortable) return;
    const th = host.shadowRoot.querySelector(`[part="th"][data-col-idx="${ci}"]`);
    if (!th) return;
    th.addEventListener('click', (e) => {
      if (_MANUAL_SLOT_SUPPORTED && e.composedPath().includes(col)) return;
      col._handleClick();
    });
    th.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); col._handleClick(); }
    });
  });
}
